import http from '../http'

/**
 * 分页条件查询发票信息
 * @param {Object} params 查询参数
 * @param {String} [params.invoiceNumber] - 发票编号（模糊查询，可选）
 * @param {String} [params.startDate] - 开始日期（yyyy-MM-dd格式，可选）
 * @param {String} [params.endDate] - 结束日期（yyyy-MM-dd格式，可选）
 * @param {Number} [params.settlementId] - 结算ID（可选）
 * @param {Number} [params.targetId] - 目标ID（可选）
 * @param {Number} [params.page] - 页码，默认为0
 * @param {Number} [params.size] - 每页数量，默认为10
 * @param {String} [params.sortBy] - 排序字段，默认为id
 * @param {String} [params.sortDir] - 排序方向（asc/desc），默认为desc
 * @returns {Promise} 返回发票信息列表和分页信息
 */
export function query_invoices_pagination(params = {}) {
// 转换分页参数
  const queryParams = {
    page: params.page !== undefined ? params.page : (params.current ? params.current - 1 : 0),
    size: params.size || params.pageSize || 10,
    sortBy: params.sortBy || 'id',
    sortDir: params.sortDir || 'desc'
  }
  
  // 添加搜索条件
  if (params.invoiceNumber) {
    queryParams.invoiceNumber = params.invoiceNumber
  }
  if (params.startDate) {
    queryParams.startDate = params.startDate
  }
  if (params.endDate) {
    queryParams.endDate = params.endDate
  }
  if (params.settlementId) {
    queryParams.settlementId = params.settlementId
  }
  if (params.targetId) {
    queryParams.targetId = params.targetId
  }
  
  return http.get('/invoices/search', { params: queryParams })
}

/**
 * 根据ID查询发票详情
 * @param {number} id - 发票ID
 * @returns {Promise} - 返回发票详情的Promise对象，包括发票基本信息和关联结算单信息
 */
export function query_invoice_by_id(id) {
return http.get(`/invoices/${id}`)
}

/**
 * 根据结算ID查询发票列表
 * @param {number} settlementId - 结算单ID
 * @returns {Promise} - 返回发票列表的Promise对象
 */
export function query_invoices_by_settlement(settlementId) {
return http.get(`/invoices/settlement/${settlementId}`)
}

/**
 * 创建或更新发票信息（智能创建或更新）
 * @param {Object} data - 发票数据
 * @param {Number} [data.id] - 发票ID(更新时提供，新增时不提供)
 * @param {String} data.invoiceNumber - 发票编号，必填
 * @param {String} data.invoiceDate - 发票日期（yyyy-MM-dd格式），必填
 * @param {Number} data.amount - 发票金额（不含税），必填
 * @param {Number} data.taxAmount - 税费金额，必填
 * @param {Number} data.settlementAmount - 结算金额（含税总额），必填
 * @param {Number} data.targetId - 目标ID（开票对象的统一标识），必填
 * @param {Array<Number>} data.settlementIds - 关联的结算单ID列表（支持多个结算单），必填
 * @param {String} [data.remark] - 备注信息（可选）
 * @param {String} [data.screenshotPath] - 发票截图路径（可选）
 * @param {String} [data.originalFilePath] - 发票原件路径（可选）
 * @returns {Promise} - 返回保存结果的Promise对象
 */
export function save_invoice(data) {
return http.post('/invoices', data)
}


/**
 * 删除发票（逻辑删除）
 * @param {Number} id - 发票ID
 * @returns {Promise} - 返回删除结果的Promise对象
 */
export function delete_invoice(id) {
return http.delete(`/invoices/${id}`)
}

/**
 * 创建随机测试发票
 * @param {Number} settlement_id - 关联的结算单ID
 * @returns {Promise} - 返回创建结果的Promise对象
 */
export async function create_random_test_invoice(settlement_id) {
  try {
if (!settlement_id) {
      throw new Error('必须提供结算单ID')
    }
    
    // 先验证结算单ID是否存在
    try {
const checkResponse = await http.get(`/settlements/${settlement_id}`)
      
      let isValid = false;
      
      // 检查返回的数据格式，确保获取到了有效的结算单
      if (checkResponse.data && checkResponse.data.data && checkResponse.data.data.id) {
        isValid = true;
      } else if (checkResponse.data && checkResponse.data.id) {
        isValid = true;
      }
      
      if (!isValid) {
        console.error(`无法验证结算单ID ${settlement_id} 的有效性`)
        throw new Error(`结算单ID ${settlement_id} 不存在或无法访问`)
      }
      
} catch (error) {
      console.error(`验证结算单ID失败: ${error.message || error}`)
      throw new Error(`无法验证结算单ID: ${error.message || error}`)
    }
    
    // 随机生成发票金额基数(10000-20000)
    const baseAmount = Math.floor(Math.random() * 10000) + 10000
    
    // 计算税金（10%）
    const taxAmount = baseAmount * 0.1
    
    const randomData = {
      settlement_id: settlement_id,
      invoice_amount: baseAmount.toFixed(2),
      fee_tax_amount: taxAmount.toFixed(2),
      settled_amount: baseAmount.toFixed(2),
      status: "待开票"
    }
    
// 调用创建发票API
    const response = await save_invoice(randomData)
return response
  } catch (error) {
    console.error('创建随机测试发票失败:', error)
    return Promise.reject({
      success: false,
      message: '创建随机测试发票失败: ' + (error.message || '未知错误'),
      error: error.toString()
    })
  }
}

export default {
  query_invoices_pagination,
  query_invoice_by_id,
  query_invoices_by_settlement,
  save_invoice,
  delete_invoice,
  create_random_test_invoice
}